import React from 'react';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useDispatch, useSelector } from 'react-redux';

import ExportModel from '$gbusiness/models/export';
import intl from '$intl';
import { fetchApi } from '$gbusiness/services/api';
import { CsvWrapper } from './styles';
import { Button } from '../../primitives';
import { LOADING } from '$gbusiness/redux/loading/types';
import { generateGetParam, removeProp } from '$gbusiness/helpers/util';
import { toastDanger } from '$gbusiness/redux/toaster/actions';

interface ExportButtonProps {
  filter?: any;
  exportSetting?: ExportModel;
  component?: React.ReactNode;
  className?: string;
  fileName: string;
  color?: any;
  variant?: any;
  excludeIcon?: boolean;
  hideLoading?: boolean;
  data?: Array<any>;
  text?: string;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  color,
  variant,
  exportSetting,
  className,
  fileName,
  filter,
  component,
  excludeIcon = false,
  hideLoading = false,
  data,
  text = null,
}) => {
  const dispatch = useDispatch();
  const { sorting } = useSelector((state: any) => state.localStorage);

  const getList = async () => {
    if (data || !exportSetting) return data;

    const { url, sortKey, sortOrder, deriveTable, deriveToModel, method } = exportSetting;
    const param = removeProp({
      ...filter,
      pageSize: 10000,
      sortKey: sorting?.sortKey || sortKey,
      sortOrder: sorting?.sortOrder || sortOrder,
    });

    if (!hideLoading) {
      dispatch({
        type: LOADING,
        loadingText: intl('PROGRESS.EXPORTING'),
      });
    }

    const response = await fetchApi({
      url: `${url}?${generateGetParam(param)}`,
      method: method || 'POST',
      param,
    });

    if (!hideLoading) {
      dispatch({ type: 'EXPORT_SUCCESS' });
    }

    return response.list.map((item: any) => {
      const raw = deriveToModel(item);
      return deriveTable ? deriveTable(raw) : raw;
    });
  };

  const finalFileName = exportSetting?.getFileName
    ? exportSetting.getFileName(filter)
    : exportSetting?.fileName || fileName;

  const handleDirectDownload = async () => {
    if (!exportSetting?.url) return;

    if (!hideLoading) {
      dispatch({
        type: LOADING,
        loadingText: intl('PROGRESS.EXPORTING'),
      });
    }

    try {
      const param = removeProp({
        ...filter,
        pageSize: 10000,
        sortKey: sorting?.sortKey || exportSetting.sortKey,
        sortOrder: sorting?.sortOrder || exportSetting.sortOrder,
      });

      const response = await fetchApi({
        url: `${exportSetting.url}?${generateGetParam(param)}`,
        method: 'GET',
        param,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Export failed');
      }

      // Create blob and trigger download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = response.filename || `${finalFileName}.csv`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Export error:', error);
      dispatch(toastDanger(error.message || 'Export failed. Please try again.'));
    } finally {
      if (!hideLoading) {
        dispatch({ type: 'EXPORT_SUCCESS' });
      }
    }
  };

  // Determine which export method to use
  if (exportSetting?.qbFlag) {
    const buttonContent = (
      <Button
        variant={variant || 'contained'}
        startIcon={excludeIcon ? null : <GetAppIcon />}
        onClick={handleDirectDownload}
        color={color || 'secondary'}>
        {text ?? intl('BUTTON.EXPORT')}
      </Button>
    );

    return component ? <div onClick={handleDirectDownload}>{component}</div> : buttonContent;
  }

  // Standard CSV export using CsvWrapper
  return (
    <CsvWrapper
      filename={finalFileName}
      className={className}
      extension=".csv"
      separator=","
      wrapColumnChar='"'
      datas={getList}>
      {component || (
        <Button
          variant={variant || 'contained'}
          startIcon={excludeIcon ? null : <GetAppIcon />}
          onClick={() => {}}
          color={color || 'secondary'}>
          {text ?? intl('BUTTON.EXPORT')}
        </Button>
      )}
    </CsvWrapper>
  );
};

export default ExportButton;
